import React from "react";

import UpgradeIcon from "@uui/Rocket02";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Stack, Typography } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";

import { isPaidPlan } from "@app/analytics/account/Billing/utils";
import WizardTitle from "@app/analytics/account/components/WizardTitle";
import {
  WizardFormProps,
  WizardFormValues,
} from "@app/analytics/account/WorkspaceSettings/WorkspaceWizard/types";
import TextField from "@app/analytics/components/TextField";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import Checkbox from "@rui/Checkbox";
import Link from "@rui/Link";
import { Column } from "@rui/Stack";

const CreateWorkspaceStepForm = ({
  disabled,
  onSubmit,
  form,
  onCancel,
}: {
  disabled: boolean;
  onSubmit: WizardFormProps["onNextStep"];
  form: UseFormReturn<WizardFormValues>;
  onCancel: () => void;
}) => {
  const {
    formState: { errors },
    handleSubmit,
    register,
    resetField,
  } = form;
  const handleEscPress = () => resetField("workspaceName");
  const isNextDisabled = disabled || !form.watch("dataAgreement", false);

  return (
    <form onSubmit={handleSubmit(() => onSubmit())}>
      <Stack spacing={3}>
        <Stack>
          <Typography
            variant="bodySecondaryMed"
            sx={{ mb: 1, color: "shades.neutral.500" }}
          >
            Name your new organization
          </Typography>
          <TextField
            disabled={disabled}
            error={Boolean(errors.workspaceName?.message)}
            helperText={errors.workspaceName?.message}
            InputProps={{
              ...register("workspaceName"),
              placeholder: "Your workspace name, e.g., Acme Inc...",
              sx: {
                py: 0.5,
                "&.Mui-disabled": { bgcolor: "shades.neutral.50" },
              },
            }}
            sx={{ width: "600px", maxWidth: "100%" }}
            onEscPress={handleEscPress}
          />
        </Stack>
        <Column>
          <FormControlLabel
            {...register("dataAgreement")}
            control={<Checkbox />}
            label={
              <Typography variant="bodySecondaryReg">
                I have read and agree to the{" "}
                <Link href={LINKS.MARKETING.DATA_PROCESSING_ADDENDUM}>
                  Data Processing Agreement
                </Link>
              </Typography>
            }
          />
        </Column>
        <Stack direction="row" spacing={2}>
          <Button
            data-test-id="workspace-create-next-step"
            disabled={isNextDisabled}
            type="submit"
            sx={{ width: "160px" }}
          >
            Next Step
          </Button>
          <Button disabled={disabled} variant="text" onClick={onCancel}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

const CreateWorkspaceStep = ({ onNextStep, form }: WizardFormProps) => {
  const navigate = useNavigate();
  const { canCreateWorkspace } = useFeatureAccess();
  const { currentPlan } = useBillingContext();

  const handleCancel = () => {
    navigate(ROUTES.USER_SETTINGS_DEFAULT_SECTION);
  };

  const showUpgradeBanner =
    !canCreateWorkspace && !isPaidPlan(currentPlan.type);

  return (
    <Stack spacing={3}>
      <WizardTitle
        title="Create a Workspace"
        subtitle="Workspaces allow companies to build holistic views of meeting metrics, and allow for custom permissions across teams and individuals."
      />
      {showUpgradeBanner && (
        <Alert
          icon={<UpgradeIcon />}
          embedded
          severity="info"
          sx={{ color: "shades.neutral.600" }}
        >
          Upgrade to{" "}
          <Link to={ROUTES.BILLING_COMPARE_PLANS}>Read Pro or Enterprise</Link>{" "}
          to get started with Read Workspaces.
        </Alert>
      )}
      <CreateWorkspaceStepForm
        disabled={!canCreateWorkspace}
        form={form}
        onSubmit={onNextStep}
        onCancel={handleCancel}
      />
    </Stack>
  );
};

export default CreateWorkspaceStep;
